import {type AxiosInstance} from "axios";
import {createApi} from "../api/createApi.ts";
import {
    type ActivateMailServerRequest,
    type MailServerActivationResponse,
    type MailServerDetails,
    type MailServerTrialResponse,
    type SaveMailServerRequest,
    type SendActivationMailRequest,
    type SendTestMailRequest,
} from "../model/mailServer/MailServer.ts";

/** Garage or garage group — the scope the endpoints are cut along. */
export type MailServerScope = "garage" | "garage-group";

/**
 * The own outgoing mail server. No scope number in any path: the cockpit fills it
 * from the scope of the logged in user, and the scope is the path.
 *
 * Two resources per scope, and that is the point of the route: `/candidate` holds
 * what was entered, the plain path what actually sends. They part ways until the
 * activation code has been entered.
 */
class MailServerService {
    api: AxiosInstance;

    constructor () {
        this.api = createApi("/b4cockpit/mail-server");
    }

    /** The configuration that sends today, with the state of its last health check. */
    async getActive (scope: MailServerScope): Promise<MailServerDetails> {
        return await this.api.get(`/${scope}`).then(response => response.data as MailServerDetails);
    }

    /** The saved but not yet activated change; carries no server when there is none. */
    async getCandidate (scope: MailServerScope): Promise<MailServerDetails> {
        return await this.api.get(`/${scope}/candidate`).then(response => response.data as MailServerDetails);
    }

    async saveCandidate (scope: MailServerScope, request: SaveMailServerRequest): Promise<void> {
        await this.api.put(`/${scope}/candidate`, request);
    }

    /**
     * Copies a switched off configuration into a candidate, unchanged. From there it
     * takes the ordinary route: code mail, code entry, activation.
     */
    async retryCandidate (scope: MailServerScope): Promise<void> {
        await this.api.post(`/${scope}/candidate/retry`);
    }

    /** Sends the code over the saved candidate — the proof that it can deliver. */
    async sendActivationMail (
        scope: MailServerScope,
        request: SendActivationMailRequest,
    ): Promise<MailServerTrialResponse> {
        return await this.api
            .post(`/${scope}/candidate/activation-mail`, request)
            .then(response => response.data as MailServerTrialResponse);
    }

    async activate (
        scope: MailServerScope,
        request: ActivateMailServerRequest,
    ): Promise<MailServerActivationResponse> {
        return await this.api
            .post(`/${scope}/candidate/activation`, request)
            .then(response => response.data as MailServerActivationResponse);
    }

    /**
     * Tries the configuration that already sends. Nothing is written back: a test mail
     * that arrives does not reactivate a row the health check switched off.
     */
    async sendTestMail (
        scope: MailServerScope,
        request: SendTestMailRequest,
    ): Promise<MailServerTrialResponse> {
        return await this.api
            .post(`/${scope}/test-mail`, request)
            .then(response => response.data as MailServerTrialResponse);
    }

    /**
     * Gives up the own server. Takes effect at once — there is no candidate round trip,
     * because falling back to the group or to our own server cannot break the sending.
     */
    async remove (scope: MailServerScope): Promise<void> {
        await this.api.delete(`/${scope}`);
    }

    /** Throws away a pending change without touching what sends. */
    async discardCandidate (scope: MailServerScope): Promise<void> {
        await this.saveCandidate(scope, {transport: "SMTP", shouldDelete: true});
    }
}

const mailServerServiceInstance = new MailServerService();

export default mailServerServiceInstance;
